import React, {useEffect, useState} from 'react'
import {Center, Chip, Divider, Input, Modal, Select, Text} from "@mantine/core";
import {Button} from "@nextui-org/react";
import {useForm} from "@mantine/form";
import {useRouter} from 'next/navigation'
import {DatePickerInput} from "@mantine/dates";
import '@mantine/dates/styles.css';
import "dayjs/locale/zh-tw";

interface IIndexSearchProps {
    opened: boolean,
    close: () => void,
}

export interface ISearchForm {
    nowPage?: number;
    queryText?: string | null;
    queryType?: string;
    tagName?: string;
    fee?: string;
    domain?: string;
    activityType?: number[];
    state?: number[];
    dateSt?: Date | null;
    dateEn?: Date | null;
}

export interface ISearchInputForm {
    queryText: string,
    queryType: string,
    domain: string,
    fee: string,
    activityType: {[key: number]: boolean},
    state: {[key: number]: boolean},
    dateSt?: Date | null;
    dateEn?: Date | null;
}

const IndexSearch: React.FC<IIndexSearchProps> = ({ opened, close }) => {
    const router = useRouter();

    const form = useForm<ISearchInputForm>({
        mode: 'controlled',
        initialValues: {
            queryText: "",
            queryType: "1",
            domain: "0",
            fee: "0",
            activityType: { 1: false, 2: false, 3: false },
            state: { 1: false, 2: false, 3: false },
            dateSt: null,
            dateEn: null,
        }
    });

    function convertToArray(data: Record<string, boolean>): number[] {
        return Object.entries(data)
            .filter(([_, value]) => value) // 過濾值為 true 的項目
            .map(([key]) => parseInt(key)); // 將鍵轉換為數字
    }

    const onSubmit = () => {
        const formValues = form.getValues();
        const searchForm: ISearchForm = {}
        searchForm.queryText = formValues.queryText;
        searchForm.queryType = formValues.queryType;
        searchForm.domain = formValues.domain;
        searchForm.fee = formValues.fee;
        searchForm.activityType = convertToArray(formValues.activityType);
        searchForm.state = convertToArray(formValues.state);
        searchForm.dateSt = formValues.dateSt;
        searchForm.dateEn = formValues.dateEn;
        close();
        router.push(`/search?searchForm=${encodeURIComponent(JSON.stringify(searchForm))}`);
    }

    useEffect(() => {
        console.log(">>>>>> search page");
    }, []);

    const searchTypeData = [
        { value: '1', label: '查詢活動名稱' },
        { value: '2', label: '查詢活動單位' },
    ];

    return (
        <Modal opened={opened} onClose={close} title="活動探索" radius="sm" size="lg">
            <Modal.Body className="p-0">
                <form className="space-y-3" onSubmit={form.onSubmit(onSubmit)}>


                    <div className="flex gap-1">
                        <div className="flex-1">
                            <Input size="lg" placeholder="請輪入查詢字詞..."
                                   key={form.key('queryText')}
                                   {...form.getInputProps('queryText')}
                            ></Input>
                        </div>
                        <div className="w-[200px]">
                            <Select
                                size={"lg"}
                                data={searchTypeData}
                                defaultValue={"1"}
                                key={form.key('queryType')}
                                {...form.getInputProps('queryType')}
                                allowDeselect={false}
                            >
                            </Select>
                        </div>
                    </div>
                    <div className="flex flex-col gap-5 pt-4">
                        <div className="flex gap-1 justify-between mt-3">
                            <Center><Text c="dark">學門領域</Text></Center>
                            <div className="flex gap-3">
                                <Chip.Group
                                    key={form.key('domain')}
                                    {...form.getInputProps('domain')}
                                >
                                    <Chip value="0">全選</Chip>
                                    <Chip value="1">自然科學／應用科學／醫學</Chip>
                                    <Chip value="2">人文與社會科學</Chip>
                                </Chip.Group>
                            </div>
                        </div>

                        <div className="flex gap-1 justify-between">
                            <Center><Text c="dark">費用情形</Text></Center>
                            <div className="flex gap-3">
                                <Chip.Group
                                    key={form.key('fee')}
                                    {...form.getInputProps('fee')}
                                >
                                    <Chip value="0">全選</Chip>
                                    <Chip value="1">免費</Chip>
                                    <Chip value="2">付費</Chip>
                                </Chip.Group>
                            </div>
                        </div>

                        <div className="flex gap-1 justify-between">
                            <Center><Text c="dark">活動舉辦形式</Text></Center>
                            <div className="flex gap-3">
                                <Chip value="1"
                                      {...form.getInputProps('activityType.1', {type: 'checkbox'})}
                                >線上</Chip>
                                <Chip value="2"
                                      {...form.getInputProps('activityType.2', {type: 'checkbox'})}
                                >實體</Chip>
                                <Chip value="3"
                                      {...form.getInputProps('activityType.3', {type: 'checkbox'})}
                                >混合</Chip>
                            </div>
                        </div>
                        <div className="flex gap-1 justify-between">
                            <Center><Text c="dark">狀態</Text></Center>
                            <div className="flex gap-3">
                                <Chip value="1"
                                      disabled={form.getValues().dateSt !== null || form.getValues().dateEn !== null}
                                      {...form.getInputProps('state.1', {type: 'checkbox'})}
                                >尚未開始</Chip>
                                <Chip value="2"
                                      disabled={form.getValues().dateSt !== null || form.getValues().dateEn !== null}
                                      {...form.getInputProps('state.2', {type: 'checkbox'})}
                                >進行中</Chip>
                                <Chip value="3"
                                      disabled={form.getValues().dateSt !== null || form.getValues().dateEn !== null}
                                      {...form.getInputProps('state.3', {type: 'checkbox'})}
                                >已結束</Chip>
                            </div>
                        </div>

                        <div className="flex gap-1 justify-between">
                            <Center><Text c="dark">活動日期</Text></Center>
                            <div className="flex gap-3">
                                <div className="flex-grow flex gap-3 items-center">
                                    <div className="w-[200px] flex gap-2 items-center">
                                        <Text size="xs" c="dimmed">起</Text>
                                        <DatePickerInput
                                            size="md"
                                            placeholder="請選擇活動日期(起)"
                                            className="w-full"
                                            valueFormat="YYYY年MM月DD日"
                                            clearable
                                            disabled={Object.values(form.getValues().state).includes(true)}
                                            key={form.key('dateSt')}
                                            {...form.getInputProps('dateSt')}
                                        />
                                    </div>
                                    <div className="relative -top-1">—</div>
                                    <div className="w-[200px] flex gap-2 items-center">
                                        <Text size="xs" c="dimmed">迄</Text>
                                        <DatePickerInput
                                            size="md"
                                            placeholder="請選擇活動日期 (迄)"
                                            className="w-full"
                                            valueFormat="YYYY年MM月DD日"
                                            clearable
                                            disabled={Object.values(form.getValues().state).includes(true)}
                                            key={form.key('dateEn')}
                                            {...form.getInputProps('dateEn')}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>

                    <Divider pb="lg" variant="dashed"/>
                    <div className="flex gap-3">
                        <Button size="md" variant="flat" color="default" className="w-1/2" radius="full" onClick={() => form.reset()}>清除</Button>
                        <Button size="md" variant="flat" color="primary" className="w-1/2" radius="full"
                                type="submit">查詢</Button>
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    )
}
export default IndexSearch
